import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { IoArrowBackSharp } from "react-icons/io5";
import { MdOutlineAddCircleOutline } from "react-icons/md";
import { TbTrash, TbTrashOff } from "react-icons/tb";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import Select from "react-select";
import { toast } from "react-toastify";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import swal from "sweetalert";
import {
  applyClassToCharacters,
  CheckPermission,
  customOption,
  customSingleValue,
  getStringFromJSX,
  LoadingSpinner,
} from "../../../constants/const";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles, GetSelectedLanguage } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import { ApiFund } from "../ApiFund";

// ---------------- Reusable Components ----------------
const CustomSelect = ({
  label,
  name,
  options,
  value,
  onChange,
  loading,
  error,
  placeholder,
  isDisabled,
}) => (
  <FormGroup>
    <Label className="col-form-label">
      {<GetLanguageString props={label} />}
    </Label>
    <Select
      components={{ Option: customOption, SingleValue: customSingleValue }}
      options={options}
      name={name}
      value={value}
      className="basic-select"
      classNamePrefix="select"
      isLoading={loading}
      isDisabled={isDisabled || options.length === 0}
      onChange={onChange}
      placeholder={placeholder}
      {...(error && { invalid: true })}
    />
    {error && <h4 className="text-error">{error}</h4>}
  </FormGroup>
);

const CustomInput = ({ label, name, value, onChange, error, ...rest }) => (
  <FormGroup>
    <Label className="col-form-label">
      {<GetLanguageString props={label} />}
    </Label>
    <Input
      name={name}
      type="text"
      value={value}
      onChange={onChange}
      className="no-drag"
      {...rest}
    />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

// ---------------- Main Component ----------------
const OtherIncome = ({ history }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const unitTypeName = encryptStorage.getItem("unitTypeName");

  // ---------- State ----------
  const [loading, setLoading] = useState({
    source: false,
    accounts: false,
    dimensions: false,
    submit: false,
  });

  const [financeCategories, setFinanceCategories] = useState([]);
  const [financeAccounts, setFinanceAccounts] = useState([]);
  const [financeDimensions, setFinanceDimensions] = useState([]);
  // const [bankAccountList, setBankAccountList] = useState([]);

  const [formData, setFormData] = useState({
    selectedFinanceCategory: null,
    // selectedBankAccount: null,
    date: new Date(),
    memo: "",
    amount: "",
    serialNumber: "",
    receiptNumber: "",
    entries: [
      {
        financeAccount: null,
        dimension2: null,
        amount: "",
        quantity: "",
        price: "",
        lineMemo: "",
      },
    ],
  });

  const [errors, setErrors] = useState({});
  const [receiptType, setReceiptType] = useState("physical");

  const viewReceipt = (transactionNo) => {
    return new Promise((resolve, reject) => {
      const toastId = toast.loading(t("receipt_loading"));

      ApiFund()
        .GenerateReceipt(transactionNo, "otherIncome")
        .then((res) => {
          const blob = new Blob([res.data], { type: "application/pdf" });
          const blobUrl = URL.createObjectURL(blob);

          // Open in new tab
          window.open(blobUrl, "_blank");

          // Clean up the object URL after the tab has had time to load it
          setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);

          toast.update(toastId, {
            render: getStringFromJSX(
              applyClassToCharacters(
                getStringFromJSX(
                  <GetLanguageString props={"receipt_opened"} />,
                ),
                "ur",
              ),
            ),
            type: "success",
            isLoading: false,
            autoClose: 3000,
          });
          setTimeout(() => history.goBack(), 4000);
          resolve(true);
        })
        .catch((err) => {
          console.error(`Error viewing receipt: ${err}`);
          toast.update(toastId, {
            render: `Failed to open receipt: ${err.message}`,
            type: "error",
            isLoading: false,
            autoClose: 5000,
          });
          reject(err);
        });
    });
  };
  // ---------- Handlers ----------
  const handleChange = (field, value) => {
    if (field === "selectedFinanceCategory") {
      setFormData((prev) => ({
        ...prev,
        selectedFinanceCategory: value,
        entries: [
          {
            financeAccount: null,
            dimension2: null,
            amount: "",
            quantity: "",
            price: "",
            lineMemo: "",
          },
        ],
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }

    setErrors((prev) => ({ ...prev, [field]: "", entries: "" }));
  };

  const getTotalAmount = (entries) => {
    const total = entries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0,
    );
    return Number.isInteger(total) ? total.toString() : total.toFixed(2);
  };

  const handleEntryChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedEntries = prev.entries.map((entry, idx) =>
        idx === index ? { ...entry, [field]: value } : entry,
      );
      return { ...prev, entries: updatedEntries };
    });
    setErrors((prev) => ({ ...prev, entries: "" }));
  };

  const addEntry = () => {
    setFormData((prev) => ({
      ...prev,
      entries: [
        ...prev.entries,
        {
          financeAccount: financeAccounts[0] || null,
          dimension2: financeDimensions[0] || null,
          amount: "",
          quantity: "",
          price: "",
          lineMemo: "",
        },
      ],
    }));
  };

  const removeEntry = (index) => {
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.filter((_, idx) => idx !== index),
    }));
  };

  // Helper function to pad numbers to 4 digits with leading zeros
  const padTo4Digits = (value) => {
    if (!value) return "0000";
    return String(value).padStart(4, "0");
  };

  const handleReceiptNumber = (e) => {
    let { value } = e.target;
    value = value.replace(/\D/g, "");
    setFormData((prev) => ({
      ...prev,
      receiptNumber: value,
    }));
    setErrors((prev) => ({ ...prev, receiptNumber: "" }));
  };

  const handleReceiptTypeChange = (e) => {
    const value = e.target.value;
    setReceiptType(value);
    if (value === "digital") {
      setFormData((prev) => ({
        ...prev,
        receiptNumber: "",
      }));
      setErrors((prev) => ({ ...prev, receiptNumber: "" }));
    }
  };

  const isMaktaba = formData.selectedFinanceCategory?.label1 === "maktaba";

  const showQuantityPrice =
    formData.selectedFinanceCategory?.label1 === "asset" || isMaktaba;

  const disableIncomeHead =
    formData.selectedFinanceCategory?.label1 === "maktaba" ||
    formData.selectedFinanceCategory?.label1 === "misc";

  const isTarbiatiCourse =
    formData.selectedFinanceCategory?.label1 === "tarbiati_course";

  const showIncomeSubHead =
    !isTarbiatiCourse && formData.selectedFinanceCategory?.label1 !== "misc";

  const isLoanIndividual =
    formData.selectedFinanceCategory?.label1 === "loan_individual";

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const isValid = validate();
    if (!isValid) {
      return;
    }

    const totalAmount = isLoanIndividual
      ? formData.amount
      : getTotalAmount(formData.entries);

    const entriesPayload = isLoanIndividual
      ? [
          {
            financeAccount: formData.entries[0]?.financeAccount ?? null,
            dimension2: formData.entries[0]?.dimension2 ?? null,
            amount: formData.amount,
            lineMemo: formData.memo,
          },
        ]
      : formData.entries.map((entry) => {
          const fallbackMemo = entry.lineMemo || formData.memo;
          return {
            financeAccount: entry.financeAccount ?? null,
            dimension2: entry.dimension2 ?? null,
            amount: entry.amount,
            lineMemo: showQuantityPrice
              ? `-${padTo4Digits(entry.quantity)} x ${padTo4Digits(entry.price)}${fallbackMemo ? ` (${fallbackMemo})` : ""}`
              : fallbackMemo,
          };
        });

    const payload = {
      // bankAccount: formData.selectedBankAccount ?? null,
      category: formData.selectedFinanceCategory ?? null,
      date: formData.date,
      amount: totalAmount,
      serialNumber: formData.serialNumber,
      memo: formData.memo,
      unitId: loggedInUnitId,
      receiptNumber: formData.receiptNumber,
      generateDigitalReceipt: receiptType === "digital",
      entries: entriesPayload,
    };

    if (entriesPayload.length === 1) {
      payload.financeAccount = entriesPayload[0].financeAccount;
      payload.dimension2 = entriesPayload[0].dimension2;
    }

    setLoading((prev) => ({ ...prev, submit: true }));
    ApiFinanceLocal()
      .AddOtherIncome(payload)
      .then((res) => {
        const data = res.data;

        if (data?.statusCode === 200) {
          const transactionNo = data?.transactionNo;
          const shouldShowReceipt =
            transactionNo && Number(transactionNo) !== 0;

          swal({
            title: "Success",
            text: t(data.message) || t("funds_other_income_added_successfully"),
            icon: "success",
            buttons: shouldShowReceipt
              ? {
                  OK: true,
                  viewReceipt: {
                    text: t("common_view_receipt") || "View Receipt",
                    value: "viewReceipt",
                  },
                }
              : "OK",
            className: GetSelectedLanguage() === "en" ? "" : "ur",
          }).then((value) => {
            if (value === "viewReceipt") {
              viewReceipt(transactionNo);
              return;
            }
            history.goBack();
          });
          setLoading((prev) => ({ ...prev, submit: false }));
          return;
        }

        swal({
          title: "Error",
          text: data?.message || "Failed to add Other Income",
          icon: "error",
          buttons: "OK",
        });

        setLoading((prev) => ({ ...prev, submit: false }));
      })
      .catch((err) => {
        const errorMessage =
          err?.response?.data?.message ||
          err?.response?.data ||
          err?.message ||
          "Unable to connect to server. Please try again.";

        swal({
          title: "Error",
          text: errorMessage,
          icon: "error",
          buttons: "OK",
        });
        setLoading((prev) => ({ ...prev, submit: false }));
      });
  };

  // ---------- Effects ----------
  // Fetch bank accounts
  // useEffect(() => {
  //   if (!loggedInUnitId) return;

  //   ApiFinanceLocal()
  //     .GetFinanceBankAccounts(loggedInUnitId)
  //     .then((res) => {
  //       const accounts = res.data || [];
  //       setBankAccountList(accounts);
  //       setFormData((prev) => ({
  //         ...prev,
  //         selectedBankAccount: res.data[0] || null,
  //       }));
  //     })
  //     .catch(console.error);
  // }, [loggedInUnitId]);

  // Fetch finance categories on mount
  useEffect(() => {
    setLoading((prev) => ({ ...prev, source: true }));
    ApiFinance()
      .GetFinanceAccountCategoriesListByType("income")
      .then((res) => {
        const filtered = (res.data || []).filter((cat) => {
          // Exclude Markaz, Tanzeem, Help always
          if (
            cat.label1.includes("markaz") ||
            cat.label1.includes("tanzeem") ||
            cat.label1.includes("help") ||
            cat.label1.includes("reimbursment")
          ) {
            return false;
          }

          // If unitTypeName is not Halqa, exclude labels with Course
          if (unitTypeName !== "Halqa" && cat.label1.includes("course")) {
            return false;
          }
          if (cat.label1 === "infaq") {
            return false;
          }

          return true; // keep all other categories
        });

        setFinanceCategories(filtered);

        setFormData((prev) => ({
          ...prev,
          selectedFinanceCategory: filtered[0] || null,
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: err,
          text: err.response?.data,
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, source: false })));
  }, [unitTypeName]);

  // Fetch accounts & dimensions when category changes
  useEffect(() => {
    const categoryId = formData.selectedFinanceCategory?.value;
    if (!categoryId) return;

    setLoading((prev) => ({ ...prev, accounts: true, dimensions: true }));
    setFinanceAccounts([]);
    setFinanceDimensions([]);
    setFormData((prev) => ({
      ...prev,
      entries: [
        {
          financeAccount: null,
          dimension2: null,
          amount: "",
          quantity: "",
          price: "",
          lineMemo: "",
        },
      ],
    }));

    // Accounts
    ApiFinance()
      .GetFinanceCategoryAccountsById(categoryId)
      .then((res) => {
        setFinanceAccounts(res.data || []);
        setFormData((prev) => ({
          ...prev,
          entries: prev.entries.map((entry) => ({
            ...entry,
            financeAccount: res.data[0] || null,
          })),
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: err,
          text: err.response?.data,
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, accounts: false })));

    // Dimensions
    ApiFinance()
      .GetFinanceCategoryDimensionsById(categoryId)
      .then((res) => {
        setFinanceDimensions(res.data || []);
        setFormData((prev) => ({
          ...prev,
          entries: prev.entries.map((entry) => ({
            ...entry,
            dimension2: res.data[0] || null,
          })),
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: err,
          text: err.response?.data,
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, dimensions: false })));
  }, [formData.selectedFinanceCategory]);

  // ---------- Validation ----------
  const validate = () => {
    const temp = {};
    const totalAmount = isLoanIndividual
      ? formData.amount
      : getTotalAmount(formData.entries);
    const hasInvalidEntry = formData.entries.some((entry) => {
      if (!entry.financeAccount || !entry.amount || Number(entry.amount) <= 0) {
        return true;
      }
      if (showQuantityPrice && (!entry.quantity || !entry.price)) {
        return true;
      }
      if (showIncomeSubHead && !entry.dimension2) {
        return true;
      }
      return false;
    });

    temp.entries =
      !isLoanIndividual &&
      (formData.entries.length === 0 || hasInvalidEntry) ? (
        <GetLanguageString props="funds_add_fund_amount_paid_error" />
      ) : (
        ""
      );

    temp.receiptNumber =
      receiptType === "physical" ? (
        formData.receiptNumber ? (
          ""
        ) : (
          <GetLanguageString props="common_field_required_error" />
        )
      ) : (
        ""
      );

    if (!showQuantityPrice) {
      temp.amount = totalAmount ? (
        ""
      ) : (
        <GetLanguageString props="funds_add_fund_amount_paid_error" />
      );
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  // ---------- Render ----------
  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox">
        <div className="ibox-title d-flex justify-content-between align-items-center">
          <h5>{<GetLanguageString props="funds_other_income" />}</h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>

        <div className="ibox-content">
          {loading.source || loading.submit ? (
            LoadingSpinner()
          ) : (
            <Form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col md="3">
                  <CustomSelect
                    name="selectedFinanceCategory"
                    label="funds_source"
                    options={financeCategories}
                    value={formData.selectedFinanceCategory}
                    onChange={(val) =>
                      handleChange("selectedFinanceCategory", val)
                    }
                    error={errors.selectedFinanceCategory}
                    placeholder={t("member_select")}
                  />
                </Col>
                {isLoanIndividual && (
                  <Col md="3">
                    <CustomInput
                      name="serialNumber"
                      label="notifications_rafiq_serial_number"
                      value={formData.serialNumber}
                      onChange={(e) =>
                        handleChange("serialNumber", e.target.value)
                      }
                      error={errors.serialNumber}
                    />
                  </Col>
                )}
                {/* <Col md="3">
                  <CustomSelect
                    name="selectedBankAccount"
                    label="fund_bank_account"
                    options={bankAccountList}
                    value={formData.selectedBankAccount}
                    onChange={(val) => handleChange("selectedBankAccount", val)}
                    error={errors.selectedBankAccount}
                    placeholder={t("member_select")}
                  />
                </Col> */}
                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_date" />}
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.date}
                      maxDate={new Date()}
                      onChange={(date) =>
                        handleChange("date", date ?? new Date())
                      }
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>
                </Col>
                <Col md={"3"}>
                  {" "}
                  <FormGroup>
                    {" "}
                    <div
                      className="col-form-label d-flex align-items-center"
                      style={{ gap: 16 }}
                    >
                      <Label check className="mb-0">
                        <Input
                          type="radio"
                          name="otherIncomeReceiptType"
                          value="physical"
                          checked={receiptType === "physical"}
                          onChange={handleReceiptTypeChange}
                        />{" "}
                        {<GetLanguageString props="funds_physical_receipt" />}
                      </Label>
                      <Label check className="mb-0">
                        <Input
                          type="radio"
                          name="otherIncomeReceiptType"
                          value="digital"
                          checked={receiptType === "digital"}
                          onChange={handleReceiptTypeChange}
                        />{" "}
                        {<GetLanguageString props="funds_digital_receipt" />}
                      </Label>
                    </div>
                    <Input
                      autoFocus
                      type="text"
                      className="no-drag"
                      value={formData.receiptNumber}
                      onChange={handleReceiptNumber}
                      disabled={receiptType === "digital"}
                    />{" "}
                    {errors.receiptNumber && (
                      <h4 className="text-error">{errors.receiptNumber}</h4>
                    )}{" "}
                  </FormGroup>{" "}
                </Col>{" "}
                <Col md="3">
                  <CustomInput
                    name="memo"
                    label="transactions_memo"
                    value={formData.memo}
                    onChange={(e) => handleChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>
                {isLoanIndividual && (
                  <Col md="3">
                    <CustomInput
                      name="amount"
                      label="funds_add_fund_amount"
                      value={formData.amount}
                      onChange={(e) => handleChange("amount", e.target.value)}
                      error={errors.amount}
                    />
                  </Col>
                )}{" "}
              </Row>
              {!isLoanIndividual && (
                <Row className="mb-3">
                  <Col md="3">
                    <CustomInput
                      name="totalAmount"
                      label="finance_other_income_total_amount"
                      value={getTotalAmount(formData.entries)}
                      readOnly
                      disabled
                      error={errors.amount}
                    />
                  </Col>
                </Row>
              )}
              {!isLoanIndividual && (
                <div className="ibox">
                  <div className="ibox-title d-flex justify-content-between align-items-center">
                    <h5>
                      {
                        <GetLanguageString
                          props={
                            isMaktaba
                              ? "finance_other_income_maktaba_sale_details"
                              : "finance_other_income_entry_details"
                          }
                        />
                      }
                    </h5>
                      <MdOutlineAddCircleOutline
                        size={25}
                        color="#3ba147"
                        onClick={addEntry}
                        style={{ marginRight: 6, marginBottom: 1 }}
                      />
                  </div>
                  <div className="ibox-content">
                    {formData.entries.map((entry, index) => (
                      <Row key={index} className="mb-3 align-items-end">
                        <Col md={"3"}>
                          <CustomSelect
                            name={`financeAccount-${index}`}
                            label="funds_income_head"
                            options={financeAccounts}
                            value={entry.financeAccount}
                            onChange={(val) =>
                              handleEntryChange(index, "financeAccount", val)
                            }
                            error={errors.entries}
                            loading={loading.accounts}
                            isDisabled={disableIncomeHead}
                            placeholder={
                              loading.accounts
                                ? t("finance_other_income_income_heads_loading")
                                : financeAccounts.length > 0
                                  ? t(
                                      "finance_other_income_income_heads_select",
                                    )
                                  : t("finance_other_income_income_heads_empty")
                            }
                          />
                        </Col>
                        {showIncomeSubHead && (
                          <Col md={"3"}>
                            <CustomSelect
                              name={`dimension2-${index}`}
                              label="funds_income_sub_head"
                              options={financeDimensions}
                              value={entry.dimension2}
                              onChange={(val) =>
                                handleEntryChange(index, "dimension2", val)
                              }
                              error={errors.entries}
                              loading={loading.dimensions}
                              placeholder={
                                loading.dimensions
                                  ? t(
                                      "finance_other_income_income_sub_heads_loading",
                                    )
                                  : financeDimensions.length > 0
                                    ? t(
                                        "finance_other_income_income_sub_heads_select",
                                      )
                                    : t(
                                        "finance_other_income_income_sub_heads_empty",
                                      )
                              }
                            />
                          </Col>
                        )}
                        <Col md={showQuantityPrice ? "1" : "2"}>
                          <CustomInput
                            name={`entryAmount-${index}`}
                            label="funds_add_fund_amount"
                            value={entry.amount}
                            onChange={(e) =>
                              handleEntryChange(index, "amount", e.target.value)
                            }
                            error={errors.entries}
                          />
                        </Col>
                        {showQuantityPrice && (
                          <>
                            <Col md="1">
                              <CustomInput
                                name={`quantity-${index}`}
                                label="finance_other_income_quantity"
                                value={entry.quantity}
                                onChange={(e) =>
                                  handleEntryChange(
                                    index,
                                    "quantity",
                                    e.target.value,
                                  )
                                }
                                error={errors.entries}
                              />
                            </Col>
                            <Col md={isMaktaba ? "2" : "1"}>
                              <CustomInput
                                name={`price-${index}`}
                                label="finance_other_income_price"
                                value={entry.price}
                                onChange={(e) =>
                                  handleEntryChange(
                                    index,
                                    "price",
                                    e.target.value,
                                  )
                                }
                                error={errors.entries}
                              />
                            </Col>
                          </>
                        )}
                        {!isMaktaba && (
                          <Col md={"2"}>
                            <CustomInput
                              name={`lineMemo-${index}`}
                              label="transactions_memo"
                              value={entry.lineMemo}
                              onChange={(e) =>
                                handleEntryChange(
                                  index,
                                  "lineMemo",
                                  e.target.value,
                                )
                              }
                              error={errors.entries}
                            />
                          </Col>
                        )}
                        <Col
                          md="auto"
                          style={{ position: "relative", bottom: "23px" }}
                          className="text-center"
                        >
                          {formData.entries.length === 1 ? (
                            <TbTrashOff
                              size={25}
                              color="#a72424"
                              style={{ opacity: 0.5 }}
                            />
                          ) : (
                            <TbTrash
                              size={25}
                              color="#bb0e0e"
                              style={{ cursor: "pointer" }}
                              onClick={() => removeEntry(index)}
                            />
                          )}
                        </Col>
                      </Row>
                    ))}
                  </div>
                </div>
              )}
              <Row>
                <Col md="9"></Col>
                <Col md="3" className="text-end">
                  {CheckPermission("CanAddFunds") ? (
                    <>
                      <Button color="white" onClick={() => history.goBack()}>
                        {<GetLanguageString props="common_cancel" />}
                      </Button>
                      &nbsp;&nbsp;
                      <Button
                        disabled={Object.values(loading).some(
                          (val) => val === true,
                        )}
                        color="primary"
                        onClick={() => handleSubmit(false)}
                      >
                        {<GetLanguageString props="funds_add_income" />}
                      </Button>
                    </>
                  ) : null}
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(OtherIncome);
